(ns arthur.ui.settings
  "The two dialogs that decide a document's rate: making one, and changing it.

  A RATE IS NOT AN INPUT. Typing 12 over 30 used to rewrite the document as you
  typed, and what it did to the symbols already in it was a guess the editor made
  on your behalf. Changing it now asks the one question that matters — keep the
  frames, or keep the time — and says what each does, before anything moves."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.palette :as pal]
            [arthur.events.collab :as collab]
            [arthur.events.project :as project]
            [arthur.subs.playback :as playback]
            [arthur.ui.palette :as palette]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(def rates [8 12 15 24 30])

(def sizes [[320 200] [640 360] [1280 720] [1920 1080]])

(defn- whole [s] (let [n (js/parseInt s 10)] (when (pos? n) n)))

(defn- chips
  "One button per choice, the chosen one lit, and a field for anything else."
  [label choices chosen show choose! & [field]]
  [:div.export-field label
   [:div.chips
    (doall (for [c choices]
             ^{:key (show c)}
             [:button {:class (when (= c chosen) "on") :on-click #(choose! c)} (show c)]))
    field]])

(defn- number [value on-number label]
  [:input.chip-number {:type "number" :min 1 :step 1 :value (or value "") :aria-label label
                       :on-change #(on-number (whole (.. % -target -value)))}])

(defn- dialog [title close! & body]
  [:<> [:div.export-scrim {:on-click close!}]
   (into [:section.export-dialog {:role "dialog" :aria-modal true :aria-label title}
          [:h2 title]]
         body)])

(defn new-project
  "A button that makes a project, after asking what it is: its name, rate, size
  and palette, every one already answered with the default."
  [label]
  (r/with-let [open? (r/atom false)
               draft (r/atom nil)]
    (let [{title :name chosen :palette :keys [fps width height] :as d} @draft
          close! #(reset! open? false)
          ok? (and fps width height)]
      [:<>
       [:button {:on-click #(do (reset! draft (assoc clip/blank-settings :name "" :palette pal/default-id))
                                (reset! open? true))}
        label]
       (when @open?
         [dialog "New project" close!
          [:label.export-field "name"
           [:input {:type "text" :value title :placeholder "untitled" :auto-focus true
                    :on-change #(swap! draft assoc :name (.. % -target -value))}]]
          [chips "frame rate" rates fps #(str % " fps") #(swap! draft assoc :fps %)
           [number fps #(swap! draft assoc :fps %) "frame rate"]]
          [chips "size" sizes [width height] (fn [[w h]] (str w "×" h))
           (fn [[w h]] (swap! draft assoc :width w :height h))
           [:span.chip-size
            [number width #(swap! draft assoc :width %) "width"] "×"
            [number height #(swap! draft assoc :height %) "height"]]]
          [:div.export-field "palette"
           [:div.palette-list {:role "listbox" :aria-label "palette"}
            (doall
             (for [p pal/presets]
               ^{:key (str (:id p))}
               [:div.palette-row {:class (when (= chosen (:id p)) "on")}
                [:button.choose {:role "option" :aria-selected (= chosen (:id p))
                                 :on-click #(swap! draft assoc :palette (:id p))}
                 [palette/strip p] [:span.name (:name p)]]]))]
           [:span.dim "every one comes with the project; this is the one it starts on"]]
          [:div.row
           [:button {:disabled (not ok?)
                     :on-click #(do (close!) (rf/dispatch [::collab/create d]))}
            "create"]
           [:button {:on-click close!} "cancel"]]])])))

(defn rate
  "The document's rate as a button, which opens the question of changing it."
  [disabled?]
  (r/with-let [open? (r/atom false)
               draft (r/atom nil)]
    (let [fps @(rf/subscribe [::playback/fps])
          close! #(reset! open? false)
          to @draft
          apply! (fn [mode] (close!) (rf/dispatch [::project/set-fps to mode]))
          changed? (and to (not= to fps))]
      [:<>
       [:button {:disabled disabled? :title "frame rate · change it"
                 :on-click #(do (reset! draft fps) (reset! open? true))}
        (str fps " fps")]
       (when @open?
         [dialog "Frame rate" close!
          [chips (str "from " fps " fps to") rates to #(str % " fps") #(reset! draft %)
           [number to #(reset! draft %) "frame rate"]]
          [:div.choice
           [:button {:disabled (not changed?) :on-click #(apply! :keep-frames)}
            [:strong "keep frames"]
            [:span.dim (str "every frame stays where it is and plays for 1/" (or to "…")
                            " s, so the animation runs "
                            (if (and to (< to fps)) "slower" "faster")
                            ". Sounds still play in seconds.")]]
           [:button {:disabled (not changed?) :on-click #(apply! :scale-spans)}
            [:strong "keep timing"]
            [:span.dim (str "every key, drawing and clip moves to where the same moment falls"
                            (when (and to (< to fps)) ", and frames that land together keep the later one")
                            ". Footage and tracking keep their own frames and are played to fit.")]]]
          [:div.row [:button {:on-click close!} "cancel"]]])])))
